import { useMemo, useState, type FormEvent } from 'react';
import { parties, requests } from '../api/endpoints';
import type { ClientAccount, Contact, Party, RequestEntity } from '../api/types';
import {
    Card, Column, DataTable, ErrorBox, Field, fmtDate, label, PageHeader, StatusBadge,
} from '../components/ui';
import { navigate, useAsync } from '../hooks';

const STAGES = ['', 'new', 'calculation', 'offer', 'agreed', 'in_progress', 'ready', 'success', 'refused', 'cancelled'];
const CURRENCIES = ['RUB', 'USD', 'EUR', 'CNY'];
const DOC_TYPES = ['unknown', 'ss', 'ds', 'refusal_letter', 'aip', 'sgr', 'other'];

function CreateRequestForm({ onDone }: { onDone: (id: string) => void }) {
  const accounts = useAsync<ClientAccount[]>(() => parties.clientAccounts(), []);
  const contacts = useAsync<Contact[]>(() => parties.contacts(), []);
  const allParties = useAsync<Party[]>(() => parties.list(), []);
  const [accountId, setAccountId] = useState('');
  const [contactId, setContactId] = useState('');
  const [title, setTitle] = useState('');
  const [currency, setCurrency] = useState('RUB');
  const [docType, setDocType] = useState('unknown');
  const [country, setCountry] = useState('');
  const [payerId, setPayerId] = useState('');
  const [applicantId, setApplicantId] = useState('');
  const [manufacturerId, setManufacturerId] = useState('');
  const [productName, setProductName] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [tnved, setTnved] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await requests.create({
        client_account_id: accountId,
        title,
        currency,
        contact_id: contactId || null,
        document_type: docType,
        country_of_entry: country || undefined,
        payer_id: payerId || undefined,
        applicant_id: applicantId || undefined,
        manufacturer_id: manufacturerId || undefined,
        product_name: productName || undefined,
        product_description: productDescription || undefined,
        tnved: tnved || undefined,
      });
      if (res?.request_id) onDone(res.request_id);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={onSubmit}>
      <ErrorBox error={error} />
      <Field label="Клиентский счёт" hint="Список загружается из /api/v1/parties/client-account/">
        <select value={accountId} onChange={(e) => setAccountId(e.target.value)} required>
          <option value="">— выберите —</option>
          {(accounts.data ?? []).map((a) => (
            <option key={a.id} value={a.id}>{a.party_name ?? a.party}</option>
          ))}
        </select>
      </Field>
      <div className="form-row">
        <Field label="Название первой позиции">
          <input value={title} onChange={(e) => setTitle(e.target.value)} required
                 placeholder="Например: регистрация продукции X" />
        </Field>
        <Field label="Валюта">
          <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
            {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Тип документа">
          <select value={docType} onChange={(e) => setDocType(e.target.value)}>
            {DOC_TYPES.map((d) => <option key={d} value={d}>{label(d)}</option>)}
          </select>
        </Field>
        <Field label="Страна ввоза">
          <input value={country} onChange={(e) => setCountry(e.target.value.toUpperCase())} maxLength={2} placeholder="RU" />
        </Field>
      </div>
      <div className="form-row">
        <Field label="Плательщик">
          <select value={payerId} onChange={(e) => setPayerId(e.target.value)}>
            <option value="">—</option>
            {(allParties.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <Field label="Заявитель">
          <select value={applicantId} onChange={(e) => setApplicantId(e.target.value)}>
            <option value="">—</option>
            {(allParties.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <Field label="Изготовитель">
          <select value={manufacturerId} onChange={(e) => setManufacturerId(e.target.value)}>
            <option value="">—</option>
            {(allParties.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
      </div>
      <div className="form-row">
        <Field label="Продукция">
          <input value={productName} onChange={(e) => setProductName(e.target.value)} placeholder="Наименование продукции" />
        </Field>
        <Field label="ТН ВЭД">
          <input value={tnved} onChange={(e) => setTnved(e.target.value)} placeholder="можно оставить пустым" />
        </Field>
      </div>
      <Field label="Техническое описание продукции">
        <textarea value={productDescription} onChange={(e) => setProductDescription(e.target.value)} rows={3}
                  placeholder="Описание, состав, назначение, технические параметры" />
      </Field>
      <Field label="Контакт (необязательно)">
        <select value={contactId} onChange={(e) => setContactId(e.target.value)}>
          <option value="">—</option>
          {(contacts.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </Field>
      <div className="form-actions">
        <button className="btn primary" type="submit" disabled={busy || !accountId || !title}>
          {busy ? 'Создание…' : 'Создать заявку'}
        </button>
      </div>
    </form>
  );
}

export function RequestsPage() {
  const [stage, setStage] = useState('');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const list = useAsync<RequestEntity[]>(
    () => requests.list({ stage: stage || undefined, search: search || undefined }),
    [stage, search],
  );

  const columns: Column<RequestEntity>[] = useMemo(() => [
    { key: 'number', title: '№', render: (r) => <strong>{r.number ?? '—'}</strong>, width: '110px' },
    { key: 'title', title: 'Название', render: (r) => r.title || '—' },
    { key: 'stage', title: 'Стадия', render: (r) => <StatusBadge value={r.stage} /> },
    { key: 'client_account', title: 'Клиентский счёт', render: (r) => <span className="muted">{r.client_account?.slice(0, 8)}…</span> },
    { key: 'promised_due_at', title: 'Срок', render: (r) => fmtDate(r.promised_due_at) },
    { key: 'on_hold', title: 'Пауза', render: (r) => r.on_hold ? <span className="badge warn">на паузе</span> : '—' },
    { key: 'created_at', title: 'Создана', render: (r) => fmtDate(r.created_at) },
  ], []);

  return (
    <div>
      <PageHeader title="Заявки">
        <button className="btn primary" onClick={() => setShowCreate((v) => !v)}>
          {showCreate ? 'Скрыть форму' : 'Новая заявка'}
        </button>
      </PageHeader>

      {showCreate && (
        <Card title="Создание заявки (с первой позицией)">
          <CreateRequestForm onDone={(id) => navigate(`/requests/${id}`)} />
        </Card>
      )}

      <div className="toolbar">
        <select value={stage} onChange={(e) => setStage(e.target.value)}>
          {STAGES.map((s) => <option key={s} value={s}>{s ? label(s) : 'Все стадии'}</option>)}
        </select>
        <input placeholder="Поиск по номеру…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <button className="btn" onClick={list.reload}>Обновить</button>
      </div>

      <Card>
        <ErrorBox error={list.error} onRetry={list.reload} />
        {list.loading && !list.data ? (
          <div className="loading">Загрузка заявок…</div>
        ) : (
          <DataTable
            columns={columns}
            rows={list.data ?? []}
            onRowClick={(r) => navigate(`/requests/${r.id}`)}
            empty="Заявок нет"
          />
        )}
      </Card>
    </div>
  );
}
